import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ItemService } from '../../../../services/item.service';
import { StockService } from '../../../../services/stock.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { Item, ItemCreate, ItemUpdate } from '../../../../models/Item';
import { Stock } from '../../../../models/Stock';
import { Warehouse } from '../../../../models/Warehouse';
import { StatusService } from '../../../../services/status.service';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-item-form',
  templateUrl: './item-form.component.html',
  styleUrls: ['./item-form.component.css']
})
export class ItemFormComponent implements OnInit {
  // Formulários
  formStep1!: FormGroup; // Informações básicas
  formStep2!: FormGroup; // Propriedades físicas e localização
  formStep3!: FormGroup; // Controle de estoque e financeiro
  formStep4!: FormGroup; // Status e observações

  currentStep = 1;
  totalSteps = 4;
  availableTabs = [
    { id: 'step1', label: 'Informações Básicas', icon: 'fas fa-info-circle', required: true, completed: false },
    { id: 'step2', label: 'Propriedades Físicas', icon: 'fas fa-ruler', required: false, completed: false },
    { id: 'step3', label: 'Controle de Estoque', icon: 'fas fa-cubes', required: false, completed: false },
    { id: 'step4', label: 'Status e Observações', icon: 'fas fa-cogs', required: true, completed: false }
  ];
  activeTab = 'step1';
  itemId?: number;
  isSubmitting = false;
  isEditMode = false;
  loading = false;
  error: string | null = null;

  // Dados relacionados
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  item: Item | null = null;

  // Opções para selects
  units = [
    'Unidade',
    'Peça',
    'Kit',
    'Conjunto',
    'Litro',
    'Quilograma',
    'Metro',
    'Metro Quadrado',
    'Metro Cúbico',
    'Caixa',
    'Pacote',
    'Rolo',
    'Par',
    'Dúzia',
    'Centena',
    'Milheiro'
  ];

  statusOptions: Status[] = [];

  conditionOptions = [
    'Novo',
    'Usado',
    'Danificado',
    'Reparado',
    'Refurbished',
    'Em Teste',
    'Defeituoso'
  ];

  qualityStatusOptions = [
    'Bom',
    'Regular',
    'Ruim',
    'Danificado',
    'Em Análise',
    'Aprovado',
    'Reprovado'
  ];

  currencyCodes = [
    'BRL',
    'USD',
    'EUR',
    'GBP',
    'JPY'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private itemService: ItemService,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private statusService: StatusService
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadStocks();
    this.loadWarehouses();
    this.loadStatuses();
    this.checkEditMode();
    this.syncTabFromStep();
    this.updateTabsCompletionStatus();
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Item').subscribe({
      next: (statuses) => {
        if (statuses && statuses.length > 0) {
          this.statusOptions = statuses;
        }
      },
      error: (error) => {
        console.error('Error loading statuses:', error);
      }
    });
  }

  initializeForms(): void {
    // Step 1: Informações básicas
    this.formStep1 = this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(50)]],
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      stockId: ['', Validators.required],
      warehouseId: [''],
      serialNumber: ['', Validators.maxLength(100)],
      batchNumber: ['', Validators.maxLength(100)],
      manufacturerCode: ['', Validators.maxLength(100)],
      manufacturerName: ['', Validators.maxLength(200)],
      model: ['', Validators.maxLength(50)],
      partNumber: ['', Validators.maxLength(100)]
    });

    // Step 2: Propriedades físicas e localização
    this.formStep2 = this.fb.group({
      unit: [''],
      weight: ['', [Validators.min(0)]],
      volume: ['', [Validators.min(0)]],
      length: ['', [Validators.min(0)]],
      width: ['', [Validators.min(0)]],
      height: ['', [Validators.min(0)]],
      specificLocation: ['', Validators.maxLength(200)],
      shelf: ['', Validators.maxLength(100)],
      rack: ['', Validators.maxLength(100)],
      position: ['', Validators.maxLength(100)],
      color: ['', Validators.maxLength(50)],
      size: ['', Validators.maxLength(50)]
    });

    // Step 3: Controle de estoque e financeiro
    this.formStep3 = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(0)]],
      minimumQuantity: ['', [Validators.min(0)]],
      maximumQuantity: ['', [Validators.min(0)]],
      manufactureDate: [''],
      expirationDate: [''],
      bestBeforeDate: [''],
      qualityStatus: [''],
      unitCost: ['', [Validators.min(0)]],
      totalCost: ['', [Validators.min(0)]],
      unitPrice: ['', [Validators.min(0)]],
      totalPrice: ['', [Validators.min(0)]],
      currencyCode: ['BRL'],
      costPrice: ['', [Validators.min(0)]],
      salePrice: ['', [Validators.min(0)]],
      approvalValueLimit: ['', [Validators.min(0)]],
      requiresApproval: [false]
    });

    // Step 4: Status e observações
    this.formStep4 = this.fb.group({
      status: ['Disponível', Validators.required],
      condition: [''],
      isActive: [true],
      isInUse: [false],
      isDamaged: [false],
      supplierReference: ['', Validators.maxLength(200)],
      barcode: ['', Validators.maxLength(500)],
      qrCode: ['', Validators.maxLength(500)],
      notes: ['', Validators.maxLength(1000)]
    });
  }

  checkEditMode(): void {
    this.itemId = this.route.snapshot.params['id'];
    if (this.itemId) {
      this.isEditMode = true;
      this.loadItem();
    }
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar almoxarifados:', error);
      }
    });
  }

  loadItem(): void {
    if (!this.itemId) return;

    this.loading = true;
    this.error = null;

    this.itemService.getItemById(this.itemId).subscribe({
      next: (item) => {
        this.item = item;
        this.populateForms(item);
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Erro ao carregar item';
        this.loading = false;
        console.error('Erro ao carregar item:', error);
      }
    });
  }

  populateForms(item: Item): void {
    // Step 1
    this.formStep1.patchValue({
      code: item.code,
      name: item.name,
      description: item.description,
      stockId: item.stockId,
      warehouseId: item.warehouseId,
      serialNumber: item.serialNumber,
      batchNumber: item.batchNumber,
      manufacturerCode: item.manufacturerCode,
      manufacturerName: item.manufacturerName,
      model: item.model,
      partNumber: item.partNumber
    });

    // Step 2
    this.formStep2.patchValue({
      unit: item.unit,
      weight: item.weight,
      volume: item.volume,
      length: item.length,
      width: item.width,
      height: item.height,
      specificLocation: item.specificLocation,
      shelf: item.shelf,
      rack: item.rack,
      position: item.position,
      color: item.color,
      size: item.size
    });

    // Step 3
    this.formStep3.patchValue({
      quantity: item.quantity,
      minimumQuantity: item.minimumQuantity,
      maximumQuantity: item.maximumQuantity,
      manufactureDate: item.manufactureDate ? this.formatDateForInput(item.manufactureDate) : '',
      expirationDate: item.expirationDate ? this.formatDateForInput(item.expirationDate) : '',
      bestBeforeDate: item.bestBeforeDate ? this.formatDateForInput(item.bestBeforeDate) : '',
      qualityStatus: item.qualityStatus,
      unitCost: item.unitCost,
      totalCost: item.totalCost,
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice,
      currencyCode: item.currencyCode,
      costPrice: item.costPrice,
      salePrice: item.salePrice,
      approvalValueLimit: item.approvalValueLimit,
      requiresApproval: item.requiresApproval
    });

    // Step 4
    this.formStep4.patchValue({
      status: item.status,
      condition: item.condition,
      isActive: item.isActive,
      isInUse: item.isInUse,
      isDamaged: item.isDamaged,
      supplierReference: item.supplierReference,
      barcode: item.barcode,
      qrCode: item.qrCode,
      notes: item.notes
    });
  }

  formatDateForInput(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }


  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.formStep1.valid;
      case 2:
        return this.formStep2.valid;
      case 3:
        return this.formStep3.valid;
      case 4:
        return this.formStep4.valid;
      default:
        return false;
    }
  }

  onSubmit(): void {
    if (!this.isAllStepsValid()) {
      this.error = 'Por favor, preencha todos os campos obrigatórios';
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    const itemData = this.buildItemData();

    // Debug: log dos dados sendo enviados

    if (this.isEditMode) {
      this.updateItem(itemData as ItemUpdate);
    } else {
      this.createItem(itemData as ItemCreate);
    }
  }

  isAllStepsValid(): boolean {
    return this.formStep1.valid && 
           this.formStep2.valid && 
           this.formStep3.valid && 
           this.formStep4.valid;
  }

  // Helper para converter valores numéricos adequadamente
  private parseNumericValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  buildItemData(): ItemCreate | ItemUpdate {
    const step1 = this.formStep1.value;
    const step2 = this.formStep2.value;
    const step3 = this.formStep3.value;
    const step4 = this.formStep4.value;

    const itemData = {
      ...step1,
      ...step2,
      ...step3,
      ...step4,
      // Converter strings vazias para null
      warehouseId: step1.warehouseId || null,
      serialNumber: step1.serialNumber || null,
      batchNumber: step1.batchNumber || null,
      manufacturerCode: step1.manufacturerCode || null,
      manufacturerName: step1.manufacturerName || null,
      model: step1.model || null,
      partNumber: step1.partNumber || null,
      unit: step2.unit || null,
      weight: this.parseNumericValue(step2.weight),
      volume: this.parseNumericValue(step2.volume),
      length: this.parseNumericValue(step2.length),
      width: this.parseNumericValue(step2.width),
      height: this.parseNumericValue(step2.height),
      specificLocation: step2.specificLocation || null,
      shelf: step2.shelf || null,
      rack: step2.rack || null,
      position: step2.position || null,
      color: step2.color || null,
      size: step2.size || null,
      minimumQuantity: this.parseNumericValue(step3.minimumQuantity),
      maximumQuantity: this.parseNumericValue(step3.maximumQuantity),
      manufactureDate: step3.manufactureDate || null,
      expirationDate: step3.expirationDate || null,
      bestBeforeDate: step3.bestBeforeDate || null,
      qualityStatus: step3.qualityStatus || null,
      unitCost: this.parseNumericValue(step3.unitCost),
      totalCost: this.parseNumericValue(step3.totalCost),
      unitPrice: this.parseNumericValue(step3.unitPrice),
      totalPrice: this.parseNumericValue(step3.totalPrice),
      costPrice: this.parseNumericValue(step3.costPrice),
      salePrice: this.parseNumericValue(step3.salePrice),
      approvalValueLimit: this.parseNumericValue(step3.approvalValueLimit),
      requiresApproval: step3.requiresApproval || false,
      condition: step4.condition || null,
      supplierReference: step4.supplierReference || null,
      barcode: step4.barcode || null,
      qrCode: step4.qrCode || null,
      notes: step4.notes || null
    };

    return itemData as ItemCreate | ItemUpdate;
  }

  createItem(itemData: ItemCreate): void {
    this.itemService.createItem(itemData).subscribe({
      next: (item) => {
        this.isSubmitting = false;
        this.router.navigate(['/stock/items']);
        // TODO: Mostrar mensagem de sucesso
      },
      error: (error) => {
        this.isSubmitting = false;
        this.error = 'Erro ao criar item';
        console.error('Erro ao criar item:', error);
      }
    });
  }

  updateItem(itemData: ItemUpdate): void {
    if (!this.itemId) return;

    this.itemService.updateItem(this.itemId, itemData).subscribe({
      next: (item) => {
        this.isSubmitting = false;
        this.router.navigate(['/stock/items']);
        // TODO: Mostrar mensagem de sucesso
      },
      error: (error) => {
        this.isSubmitting = false;
        this.error = 'Erro ao atualizar item';
        console.error('Erro ao atualizar item:', error);
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/stock/items']);
  }

  getStepTitle(step: number): string {
    switch (step) {
      case 1:
        return 'Informações Básicas';
      case 2:
        return 'Propriedades Físicas e Localização';
      case 3:
        return 'Controle de Estoque e Financeiro';
      case 4:
        return 'Status e Observações';
      default:
        return '';
    }
  }

  getStepDescription(step: number): string {
    switch (step) {
      case 1:
        return 'Defina as informações básicas do item, incluindo código, nome e fabricante';
      case 2:
        return 'Configure as propriedades físicas e a localização do item no almoxarifado';
      case 3:
        return 'Configure quantidades, datas e informações financeiras do item';
      case 4:
        return 'Defina o status atual e adicione observações importantes';
      default:
        return '';
    }
  }

  // Validações customizadas
  validateCode(): void {
    const code = this.formStep1.get('code')?.value;
    if (code && !this.isEditMode) {
      this.itemService.checkCodeExists(code).subscribe({
        next: (exists) => {
          if (exists) {
            this.formStep1.get('code')?.setErrors({ codeExists: true });
          }
        }
      });
    }
  }

  validateSerialNumber(): void {
    const serialNumber = this.formStep1.get('serialNumber')?.value;
    if (serialNumber) {
      this.itemService.checkSerialNumberExists(serialNumber, this.itemId).subscribe({
        next: (exists) => {
          if (exists) {
            this.formStep1.get('serialNumber')?.setErrors({ serialExists: true });
          }
        }
      });
    }
  }

  // Cálculos automáticos
  calculateTotalCost(): void {
    const quantity = this.formStep3.get('quantity')?.value || 0;
    const unitCost = this.formStep3.get('unitCost')?.value || 0;
    const totalCost = quantity * unitCost;
    this.formStep3.get('totalCost')?.setValue(totalCost);
  }

  calculateTotalPrice(): void {
    const quantity = this.formStep3.get('quantity')?.value || 0;
    const unitPrice = this.formStep3.get('unitPrice')?.value || 0;
    const totalPrice = quantity * unitPrice;
    this.formStep3.get('totalPrice')?.setValue(totalPrice);
  }

  // Métodos de navegação entre steps
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  // Métodos de validação
  isFieldInvalid(fieldName: string): boolean {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    return !!(field?.invalid && field?.touched);
  }

  getFieldError(fieldName: string): string {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    
    if (!field?.errors) return '';
    
    if (field.errors['required']) return 'Campo obrigatório';
    if (field.errors['minlength']) return `Mínimo de ${field.errors['minlength'].requiredLength} caracteres`;
    if (field.errors['maxlength']) return `Máximo de ${field.errors['maxlength'].requiredLength} caracteres`;
    if (field.errors['email']) return 'Email inválido';
    if (field.errors['min']) return `Valor mínimo é ${field.errors['min'].min}`;
    
    return 'Campo inválido';
  }

  private getFormGroupForField(fieldName: string): FormGroup | null {
    if (this.formStep1.contains(fieldName)) return this.formStep1;
    if (this.formStep2.contains(fieldName)) return this.formStep2;
    if (this.formStep3.contains(fieldName)) return this.formStep3;
    if (this.formStep4.contains(fieldName)) return this.formStep4;
    return null;
  }

  // Getters para facilitar o acesso no template
  get code() { return this.formStep1.get('code'); }
  get name() { return this.formStep1.get('name'); }
  get description() { return this.formStep1.get('description'); }
  get stockId() { return this.formStep1.get('stockId'); }
  get warehouseId() { return this.formStep1.get('warehouseId'); }
  get serialNumber() { return this.formStep1.get('serialNumber'); }
  get batchNumber() { return this.formStep1.get('batchNumber'); }
  get manufacturerCode() { return this.formStep1.get('manufacturerCode'); }
  get manufacturerName() { return this.formStep1.get('manufacturerName'); }
  get model() { return this.formStep1.get('model'); }
  get partNumber() { return this.formStep1.get('partNumber'); }
  
  get unit() { return this.formStep2.get('unit'); }
  get weight() { return this.formStep2.get('weight'); }
  get volume() { return this.formStep2.get('volume'); }
  get length() { return this.formStep2.get('length'); }
  get width() { return this.formStep2.get('width'); }
  get height() { return this.formStep2.get('height'); }
  get specificLocation() { return this.formStep2.get('specificLocation'); }
  get shelf() { return this.formStep2.get('shelf'); }
  get rack() { return this.formStep2.get('rack'); }
  get position() { return this.formStep2.get('position'); }
  get color() { return this.formStep2.get('color'); }
  get size() { return this.formStep2.get('size'); }
  
  get quantity() { return this.formStep3.get('quantity'); }
  get minimumQuantity() { return this.formStep3.get('minimumQuantity'); }
  get maximumQuantity() { return this.formStep3.get('maximumQuantity'); }
  get unitCost() { return this.formStep3.get('unitCost'); }
  get totalCost() { return this.formStep3.get('totalCost'); }
  get unitPrice() { return this.formStep3.get('unitPrice'); }
  get totalPrice() { return this.formStep3.get('totalPrice'); }
  get currencyCode() { return this.formStep3.get('currencyCode'); }
  get costPrice() { return this.formStep3.get('costPrice'); }
  get salePrice() { return this.formStep3.get('salePrice'); }
  get approvalValueLimit() { return this.formStep3.get('approvalValueLimit'); }
  get requiresApproval() { return this.formStep3.get('requiresApproval'); }
  get manufactureDate() { return this.formStep3.get('manufactureDate'); }
  get expirationDate() { return this.formStep3.get('expirationDate'); }
  get bestBeforeDate() { return this.formStep3.get('bestBeforeDate'); }
  get qualityStatus() { return this.formStep3.get('qualityStatus'); }
  
  get status() { return this.formStep4.get('status'); }
  get condition() { return this.formStep4.get('condition'); }
  get isActive() { return this.formStep4.get('isActive'); }
  get isInUse() { return this.formStep4.get('isInUse'); }
  get isDamaged() { return this.formStep4.get('isDamaged'); }
  get supplierReference() { return this.formStep4.get('supplierReference'); }
  get barcode() { return this.formStep4.get('barcode'); }
  get qrCode() { return this.formStep4.get('qrCode'); }
  get notes() { return this.formStep4.get('notes'); }

  // Método submit (alias para onSubmit)
  submit(): void {
    this.onSubmit();
  }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= this.totalSteps) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }
}
